import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { List, ListItem } from 'src/components/ListItem';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Terminal } from 'src/models/terminal/_types';
import { getTerminals } from 'src/models/terminal/terminal';

import { usePosContext } from '../PosContext';

interface SelectTerminalDialogProps {
  onSelect: (terminal: Terminal) => void;
  open: boolean;
}

const Loading = () => {
  return (
    <Stack>
      <Skeleton variant="rectangular" width="100%" height={48} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={48} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={48} sx={{ mb: 1 }} />
    </Stack>
  );
};

const Empty = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.selectTerminal',
  });

  return (
    <Stack alignItems="center" gap={2} sx={{ py: 4 }}>
      <Typography variant="body1" color="text.secondary" align="center">
        {t('noTerminals')}
      </Typography>
    </Stack>
  );
};

export function SelectTerminalDialog({
  onSelect,
  open,
}: SelectTerminalDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.selectTerminal',
  });

  const { posLocation } = usePosContext();

  const {
    data: terminals,
    isLoading: isLoadingTerminals,
    isFetching: isFetchingTerminals,
  } = useQuery({
    queryKey: ['terminals'],
    queryFn: () => getTerminals(),
    select: (data) =>
      data.filter(
        (terminal) =>
          terminal.status === 'online' &&
          terminal.locationId?.toString() === posLocation?.id.toString()
      ),
  });

  return (
    <Dialog open={open} title={t('title')} subtitle={t('description')}>
      {(isLoadingTerminals || isFetchingTerminals) && <Loading />}

      {!isLoadingTerminals && terminals?.length === 0 && <Empty />}

      {!isLoadingTerminals && (
        <List sx={{ maxHeight: '300px', overflow: 'auto' }}>
          {terminals?.map((terminal) => (
            <ListItem
              key={terminal.id}
              onClick={() => onSelect(terminal)}
              icon={icon({ name: 'credit-card', style: 'regular' })}
              primary={terminal.label}
              clickable
            />
          ))}
        </List>
      )}
    </Dialog>
  );
}
